(() => {
  if (window.HausielOrderLimit) return;
  window.HausielOrderLimit = {
    defaultMax: 10,
    async fetchCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js?hausiel_order_limit=${Date.now()}`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
      });
      if (!response.ok) throw new Error('Sepet durumu alınamadı.');
      return response.json();
    },
    quantityForProduct(cart, productId) {
      const target = String(productId || '');
      return (cart?.items || []).reduce((sum, item) =>
        String(item?.product_id || '') === target ? sum + Number(item?.quantity || 0) : sum, 0);
    },
    async canAdd(productId, quantity = 1, max = 10) {
      const safeMax = Math.max(1, Number(max || this.defaultMax) || this.defaultMax);
      if (!productId) return { allowed: true, existing: 0, remaining: safeMax, max: safeMax, cart: null };
      const cart = await this.fetchCart();
      const existing = this.quantityForProduct(cart, productId);
      const requested = Math.max(0, Number(quantity || 0));
      return {
        allowed: existing + requested <= safeMax,
        existing,
        remaining: Math.max(0, safeMax - existing),
        max: safeMax,
        cart,
      };
    },
  };
})();

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        requestAnimationFrame(() => this.syncHausielOrderLimit());
      }

      get hausielProductId() {
        return String(this.dataset.hausielProductId || this.closest('product-info')?.dataset.productId || '');
      }

      get hausielMaxOrderQuantity() {
        const configured = Number(this.dataset.hausielMaxOrderQuantity || window.HausielOrderLimit?.defaultMax || 10);
        return Number.isFinite(configured) && configured > 0 ? Math.floor(configured) : 10;
      }

      get hausielQuantityInput() {
        const sectionId = this.dataset.sectionId || this.closest('product-info')?.dataset.section || '';
        return sectionId ? document.getElementById(`Quantity-${sectionId}`) : null;
      }

      async syncHausielOrderLimit() {
        const productId = this.hausielProductId;
        if (!productId || !window.HausielOrderLimit) return;
        try {
          const state = await window.HausielOrderLimit.canAdd(productId, 0, this.hausielMaxOrderQuantity);
          const input = this.hausielQuantityInput;
          if (!input) return;

          const nativeMax = Number(input.dataset.hausielNativeMax || input.getAttribute('max'));
          if (!input.dataset.hausielNativeMax && Number.isFinite(nativeMax) && nativeMax > 0) {
            input.dataset.hausielNativeMax = String(nativeMax);
          }
          const effectiveMax = Number.isFinite(Number(input.dataset.hausielNativeMax))
            ? Math.min(state.remaining, Number(input.dataset.hausielNativeMax))
            : state.remaining;

          input.dataset.hausielMaxOrderQuantity = String(state.max);
          input.dataset.hausielRemaining = String(state.remaining);
          input.setAttribute('max', String(Math.max(0, effectiveMax)));
          input.dataset.max = String(Math.max(0, effectiveMax));

          const current = Math.max(1, Number(input.value || 1));
          const plus = input.closest('quantity-input')?.querySelector('[name="plus"]');
          if (plus) plus.disabled = effectiveMax <= 0 || current >= effectiveMax;
        } catch (_) {
          // Do not block the storefront merely because the preflight cart read failed.
        }
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const preflightData = new FormData(this.form);
        const requestedQuantity = Math.max(1, parseInt(preflightData.get('quantity'), 10) || 1);
        try {
          const gate = await window.HausielOrderLimit?.canAdd(
            this.hausielProductId,
            requestedQuantity,
            this.hausielMaxOrderQuantity
          );
          if (gate && !gate.allowed) {
            const detail = gate.existing > 0 ? ` Sepetinizde şu anda ${gate.existing} adet bulunuyor.` : '';
            this.handleErrorMessage(`Bu üründen sipariş başına en fazla ${gate.max} adet ekleyebilirsiniz.${detail}`);
            await this.syncHausielOrderLimit();
            return;
          }
        } catch (_) {
          // The actual cart request remains the source of truth if preflight cannot be read.
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const publishCartUpdateAfterRender = () => {
              const publishAfterPaint = () => {
                const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
                if (!this.error) {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: variantId,
                    cartData: response,
                  }).then(() => {
                    CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                  });
                }
                this.error = false;
                this.syncHausielOrderLimit();
              };

              if (typeof requestAnimationFrame === 'function') requestAnimationFrame(publishAfterPaint);
              else setTimeout(publishAfterPaint, 0);
            };

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                    publishCartUpdateAfterRender();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
              publishCartUpdateAfterRender();
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
